import { runInAction } from "mobx"

import type { IOrchestratedQuerySender } from "@/domains/library/orchestrated-query-interfaces"
import { OrchestratedQuery } from "@/domains/library"
import { StatsEqualizerResponse } from "@/domains/validators"

import type { ScaleStatsApiTransport } from "./scale-stats-api-transport"
import type { ScaleStatsStore } from "./scale-stats-store"
import { convertApiResponseToScaleStatsStoreDAO } from "./scale-stats-converter"

export class ScaleStatsOrchestrator {
  private loadStatsQuery: OrchestratedQuery<StatsEqualizerResponse> | undefined

  constructor(
    private readonly scaleStatsStore: ScaleStatsStore,
    private readonly scaleStatsApiTransport: ScaleStatsApiTransport,
  ) {}

  initialize(loadStatsQuery: OrchestratedQuery<StatsEqualizerResponse>) {
    this.loadStatsQuery = loadStatsQuery
  }

  get query(): OrchestratedQuery<StatsEqualizerResponse> | undefined {
    return this.loadStatsQuery
  }

  /**
   * Load stats from API (triggered by OrchestratedQuery)
   * This just fetches the data and returns it for the query system
   */
  async onLoadStatsTrigger(_query: IOrchestratedQuerySender<StatsEqualizerResponse>): Promise<StatsEqualizerResponse> {
    return await this.scaleStatsApiTransport.loadScaleStats()
  }

  /**
   * Callback for when stats query succeeds
   */
  onLoadStatsQuerySuccess(
    sender: IOrchestratedQuerySender<StatsEqualizerResponse>,
    data: StatsEqualizerResponse,
  ): void {
    try {
      runInAction(() => {
        const storeDAO = convertApiResponseToScaleStatsStoreDAO(data)
        this.scaleStatsStore.updateData(storeDAO)
      })
    } catch (error) {
      console.error("Error processing scale stats data:", error)
    }
  }

  /**
   * Callback for when stats query fails
   */
  onLoadStatsQueryFailed(_sender: IOrchestratedQuerySender<StatsEqualizerResponse>, error: Error): void {
    console.error("Scale stats query failed:", error)
  }
}
